import { useTranslation } from 'react-i18next';
import { Icon } from '../shared/components/Icon';
import { getActiveLocale } from '../shared/i18n/i18n';
import { formatRelativeTime } from '../shared/lib/formatters';
import { useOffline } from './OfflineProvider';

/**
 * The standing statement that the workspace is not live.
 *
 * It says three things, because an accountant reading a balance needs all three: that there is no
 * connection, how old the figures on screen are, and what will happen to anything they write.
 * `sessionIsOffline` distinguishes a signed-in session that lost its connection from one that was
 * opened against the device copy in the first place — the second cannot reach anything that was
 * never copied, and saying so avoids a trail of unexplained failures.
 */
export function OfflineNotice({ sessionIsOffline = false }: { sessionIsOffline?: boolean }) {
  const { t } = useTranslation();
  const locale = getActiveLocale();
  const { counts, online, servedFrom, supported, unlocked } = useOffline();

  if (online) return null;

  return (
    <div className="offline-notice" role="status" aria-live="polite">
      <Icon name="cloudOff" />
      <div className="offline-notice__copy">
        <strong>{sessionIsOffline ? t('offline.notice.offlineSession') : t('offline.notice.connectionLost')}</strong>
        <span>
          {servedFrom !== null
            ? t('offline.notice.copyAge', { when: formatRelativeTime(servedFrom, locale) })
            : t('offline.notice.copyUnknown')}
          {' '}
          {supported && unlocked
            ? t('offline.notice.draftsQueued')
            : t('offline.notice.readOnly')}
        </span>
      </div>
      {counts.unsent > 0 && (
        <span className="offline-notice__count">{t('offline.notice.waiting', { count: counts.unsent })}</span>
      )}
    </div>
  );
}
